<script setup lang="ts">
import { useLinks, usePageData } from '#imports'

definePageMeta({
    layoutBodyPadding: false
})

// Warms the SSR payload for NimbusBingoLobby's internal game queries.
// Both keys must be warmed even though the data is identical — banko has no
// featured game, so the exclusive list matches the base list, but the component
// looks up each key separately.
await Promise.all([
    useGames({
        filter: { slugs: ['bingo', 'bingo-90'] },
        key: 'bingo-games',
        lazy: false,
    }),
    useGames({
        filter: { slugs: ['bingo', 'bingo-90'] },
        key: 'exclusive-bingo-games',
        lazy: false,
    }),
])

const links = useLinks()
const { asMoney } = useFormat()
const { isLoggedIn, isExisting } = useUserInfo()
const { data: accountDetails } = useSeamlessWalletAccountDetails()
const { megaPrize } = useBingo()
const { isMobile } = useDevice()
const { site } = useCoreConfig()

const headerTitle = computed(() => {
    if (isExisting.value) {
        return 'Spil Bingo eller Banko'
    }

    // if (!isLoggedIn.value) {
    // Todo: Replace with bankoCtaHeadline
    // return featuredOffer.value?.bingoCtaHeadline || 'Spil dit favorit <em class="bingo__logo">Banko</em> spil'
    return 'Spil med om præmier i<br>vores sjove <em class="bingo__logo">Banko</em>'
    // }
})

const ctaText = computed(() => {
    // Outcommented because of missing bankoCtaButtonText data
    /* if (!isLoggedIn.value) {
        return featuredOffer.value?.bingoCtaButtonText || 'Få velkomstbonus';
    } */

    if (isLoggedIn.value && Number(accountDetails.value?.balance) === 0) {
        return 'Indbetal & Spil Banko'
    }

    return 'Spil Banko'
})

const { data: bankoPage } = await usePageData('banko')
</script>

<template>
    <NimbusBingoLobby
        :page="bankoPage"
        :seo="{
            name: 'banko',
            defaults: {
                metaTitle: `Spil Banko hos ${site.name} - Klassisk Banko Online`,
                metaDescription: `Banko på ${site.name}: Sjovt & spændende! Bankoplader kun 2,50 kr. Gevinster op til 25.000 kr. | Prøv Banko i dag og oplev hyggen | 18+.`
            }
        }"
        :bingo="{
            slug: 'bingo-90',
            headerTitle: headerTitle,
            prizePoolText: 'Præmiepulje:'
        }"
        :background="{
            color: '#B30000'
        }"
        :logo="{
            src: '/media/bingo/banko-text.png'
        }"
        :stats="{
            megaPrize: undefined,
            nextRound: 'Næste runde:',
            video: {
                src: '/media/bingo/bingo-90-gameplay.mp4',
                poster: '/media/bingo/bingo-90-gameplay-poster.png'
            }
        }"
        :winners="{
            title: 'Vindere lige nu:'
        }"
        :sheets="{
            title: '40 gode grunde til at spille vores hyggelige Banko',
            subtitle: `Du kan købe op til 40 plader til kun 2,50 kr. pr. plade - <b>og i hver runde har du chancen for at vinde megapræmien på ${asMoney(megaPrize, { decimals: 0 })}</b>`,
            image: {
                src: '/media/bingo/bingo-90-sheets.png'
            }
        }"
        :facts="{
            items: [
                `Altid <em>${asMoney(megaPrize, { decimals: 0 })}</em> i Megapræmie`,
                'Altid garanti for <em>Bankohygge</em>',
                'Spil med fra <em>kun 2,50 kr.</em>'
            ],
            cta: {
                button: {
                    text: 'Spil Bingo nu'
                }
            },
            review: {
                reviewer: {
                    avatar: '/media/trustpilot/camilla.png',
                    quote: 'En superhyggelig side med flotte, kreative spil og rigtig flot grafik. Det er SÅ hyggeligt, at man kan sidde hjemme i stuen og spille bingo uden at skulle gøre det helt store ud af sig selv',
                    name: 'Camilla Juliussen',
                },
                trustpilotImage: '/media/trustpilot/rating-5-stars.png'
            }
        }"
        :games="{
            title: 'Både Bingo og Banko!'
        }"
        :info="{
            intro: {
                title: 'Bingo, Banko & Megapræmie på 25.000kr.',
                subtitle:
                    `Banko rimer på sammenhold, hygge og fantastisk underholdning. I vores Banko-verden har vi fanget magien fra de ikoniske bankohaller, hvor hvert et “Banko!” er startskuddet til fest og farver. Vi har pakket den halve gris væk og fundet spændende pengepræmier frem.
                    <br>
                    Men den gode stemning fortsætter i vores live chat, hvor du kan småsludre og hygge med de andre Banko-entusiaster, mens spændingen stiger.
                    <br>
                    Så køb dine bankoplader og mærk den ægte Bankohygge, der gør spillet til en favorit for mange. Vi glæder os til at se dig i bankohallen!
                    `,
                image: {
                    src: '/media/bingo/banko-logo-featured.png',
                    alt: 'Banko Logo'
                },
            },
            howTo: {
                title: 'Hvordan spilles Banko?',
                items: [
                    'Du starter med at købe bankoplader. Du kan købe op til 40 bankoplader ad gangen til kun 2,50 kr./stk. Dine vinderchancer stiger, jo flere bankoplader du køber.',
                    'Når bankospillet starter, udtrækkes en række vilkårlige tal. Tallene afkrydses automatisk på dine bankoplader.',
                    'BANKO! Du vinder præmier ved at være blandt de første spillere til at få 5 tal på en vandret række og/eller hele pladen fuld. I hvert bankospil er der gevinster ved: 1 række, 2 rækker, 3 rækker, 4 rækker og fuld plade.'
                ],
            },
        }"
        :links="{
            signup: links.signup.props()
        }"
    />
</template>
